(() => {
  // The theme's own MediaCarousel maps a click on the gallery to prev/next, but
  // it bails out on coarse pointers, so on touch a tap on the photo does nothing
  // at all. Only the small zoom button opens the PhotoSwipe lightbox. This adds
  // tap-anywhere-on-the-photo to open that same lightbox on touch devices.
  const TAP_MAX_MOVE_PX = 12;
  const TAP_MAX_DURATION_MS = 500;
  // The compatibility chips sit on the photo on mobile and scroll sideways, so
  // taps and flicks there belong to that row, not to the lightbox.
  const CONTROL_SELECTOR =
    'button, a[href], input, label, [role="button"], model-viewer, video, iframe, .product-gallery-icons';

  const isTouch = () => window.matchMedia('(pointer: coarse)').matches;

  const initCarousel = (carousel) => {
    if (carousel.dataset.tapZoom === 'true') return;
    carousel.dataset.tapZoom = 'true';

    let startX = 0;
    let startY = 0;
    let startedAt = 0;
    let tracking = false;

    const reset = () => {
      tracking = false;
    };

    carousel.addEventListener(
      'pointerdown',
      (event) => {
        // Mouse input keeps the theme's own prev/next click behaviour.
        tracking = event.isPrimary && event.pointerType !== 'mouse';
        startX = event.clientX;
        startY = event.clientY;
        startedAt = event.timeStamp;
      },
      { passive: true }
    );

    // The browser fires pointercancel the moment it takes the gesture over for
    // scrolling, which is exactly how a swipe is told apart from a tap.
    carousel.addEventListener('pointercancel', reset, { passive: true });

    carousel.addEventListener(
      'pointerup',
      (event) => {
        if (!tracking) return;
        reset();

        if (!isTouch()) return;
        if (event.timeStamp - startedAt > TAP_MAX_DURATION_MS) return;
        if (Math.abs(event.clientX - startX) > TAP_MAX_MOVE_PX) return;
        if (Math.abs(event.clientY - startY) > TAP_MAX_MOVE_PX) return;
        if (event.target.closest(CONTROL_SELECTOR)) return;

        const media = event.target.closest('.product-gallery__media');
        if (!media || media.getAttribute('data-media-type') !== 'image') return;

        // PhotoSwipe is fed the visible images only, so the index has to be
        // counted the same way the theme's own zoom button counts it.
        const images = carousel.visibleItems.filter(
          (item) => item.getAttribute('data-media-type') === 'image'
        );
        const index = images.indexOf(media);
        if (index === -1) return;

        carousel.dispatchEvent(
          new CustomEvent('lightbox:open', { bubbles: true, detail: { index } })
        );
      },
      { passive: true }
    );
  };

  // Line the zoom button up with the "Geschikt voor" heading row. That block is
  // anchored to the bottom of the gallery and grows when its chips wrap, which
  // depends on viewport width and on how long the labels are in the current
  // locale, so the row can only be measured, not written as a static offset.
  // Mobile keeps the chips to one scrolling row, so there the measurement is
  // stable, but it is the same block and the same alignment either way.
  const alignZoomButton = (gallery) => {
    const zoom = gallery.querySelector('.product-gallery__zoom');
    const heading = gallery.querySelector('.product-gallery-icons > p');
    const anchor = zoom?.offsetParent;
    if (!zoom || !heading || !anchor) return;

    const headingBox = heading.getBoundingClientRect();
    // Nothing to align to on products without a compatibility list.
    if (!headingBox.height) return;

    const centre = headingBox.top + headingBox.height / 2;
    const bottom = anchor.getBoundingClientRect().bottom - centre - zoom.offsetHeight / 2;
    zoom.style.setProperty('--zoom-button-bottom', `${Math.round(bottom)}px`);
  };

  const observed = new WeakSet();

  const initGallery = (gallery) => {
    alignZoomButton(gallery);

    const icons = gallery.querySelector('.product-gallery-icons');
    if (!icons || observed.has(icons)) return;
    observed.add(icons);
    // Catches the chips rewrapping on resize, and the reflow after webfonts land.
    new ResizeObserver(() => alignZoomButton(gallery)).observe(icons);
  };

  const init = () => {
    document
      .querySelectorAll('product-gallery[allow-zoom] media-carousel')
      .forEach(initCarousel);
    document.querySelectorAll('product-gallery[allow-zoom]').forEach(initGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // The gallery is re-rendered on variant/section changes and in the theme editor,
  // so re-run on new nodes. initCarousel is idempotent; the rAF keeps a burst of
  // mutations from turning into a burst of querySelectorAll calls.
  document.addEventListener('shopify:section:load', init);

  let queued = false;
  const queueInit = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      init();
    });
  };

  new MutationObserver(queueInit).observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
